import React from "react";
import { motion } from "framer-motion";
import { useReducedMotionSafe } from "@/components/animation/AnimationSettings";
import { PALETTE, COMPLIANCE_FOOTER_LINE } from "@/components/animation/MotionShared";

/**
 * Daily Insight Cards — grid of 8 animated cards covering the spectrum of
 * CareLogix ORACLE intelligence areas. Each card carries uncertainty,
 * assumptions, and a compliance line so it remains advisory-only.
 */

const CATEGORIES = [
  {
    id: "stocks",
    icon: "📈",
    label: "Stocks to Watch",
    summary: "Mega-cap technology + healthcare leadership intact; small-caps mixed.",
    uncertainty: "Macro-sensitive — watch CPI + FOMC calendar.",
    assumptions: "Public filings, prior-session closes, current sector breadth.",
  },
  {
    id: "crypto",
    icon: "₿",
    label: "Crypto Watchlist",
    summary: "BTC range-bound; ETH relative strength holding; alts cautious.",
    uncertainty: "Liquidity thin; weekend gaps possible.",
    assumptions: "24h volume + on-chain spot flow only — no derivatives leverage assumed.",
  },
  {
    id: "fx",
    icon: "💱",
    label: "FX Setups",
    summary: "USD softer vs EUR + GBP; JPY pairs respect intraday levels.",
    uncertainty: "Central bank speakers can flip bias intraday.",
    assumptions: "OANDA-style mid prices · paper-trade context only.",
  },
  {
    id: "sports",
    icon: "🎯",
    label: "Sports Picks (Legal Only)",
    summary: "Market-implied edges in select college + Premier League games.",
    uncertainty: "Injury reports + line moves can invalidate.",
    assumptions: "21+ · jurisdiction-gated · entertainment only.",
  },
  {
    id: "healthcare",
    icon: "⚚️",
    label: "Healthcare Catalysts",
    summary: "FDA AdCom + earnings setups in oncology + cardiometabolic.",
    uncertainty: "Outcomes are binary and high-variance.",
    assumptions: "Public filings only · no MNPI · no insider information.",
  },
  {
    id: "startups",
    icon: "🚀",
    label: "Startup Opportunities",
    summary: "AI agents + creator infra + healthtech raising momentum.",
    uncertainty: "Valuations + runway sensitive to rate path.",
    assumptions: "Public funding databases only.",
  },
  {
    id: "grants",
    icon: "🎓",
    label: "Grants",
    summary: "USAID, EU Horizon, Gates Foundation cycles open.",
    uncertainty: "Eligibility narrow; align mission carefully.",
    assumptions: "Publicly listed RFPs only.",
  },
  {
    id: "risk",
    icon: "⚠️",
    label: "Risk Warnings",
    summary: "Geopolitical headlines + crude inventory swings — expect volatility.",
    uncertainty: "Headline-driven moves are unpredictable.",
    assumptions: "Public OPEC + macro indicators only.",
  },
];

export default function DailyInsightCards({ testid = "daily-insight-cards" } = {}) {
  const reduce = useReducedMotionSafe();
  return (
    <section style={S.shell} data-testid={testid}>
      <header style={S.head}>
        <span style={S.tagline}>Daily Insight Cards</span>
        <h3 style={S.title}>LOGIX-2™ Briefings</h3>
      </header>
      <div style={S.grid}>
        {CATEGORIES.map((c, i) => (
          <motion.article
            key={c.id}
            data-testid={`${testid}-${c.id}`}
            style={S.card}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={reduce ? { duration: 0 } : { delay: i * 0.04, duration: 0.42, ease: "easeOut" }}
          >
            <header style={S.cardHead}>
              <span style={S.cardIcon} aria-hidden="true">{c.icon}</span>
              <strong style={S.cardTitle}>{c.label}</strong>
            </header>
            <p style={S.summary}>{c.summary}</p>
            <div style={S.meta}>
              <div><span style={S.metaLbl}>Uncertainty</span><p style={S.metaVal}>{c.uncertainty}</p></div>
              <div><span style={S.metaLbl}>Assumptions</span><p style={S.metaVal}>{c.assumptions}</p></div>
            </div>
            <footer style={S.cardFoot}><span style={S.compliance}>{COMPLIANCE_FOOTER_LINE}</span></footer>
          </motion.article>
        ))}
      </div>
    </section>
  );
}

const S = {
  shell: { background: PALETTE.glassDark, border: `1px solid ${PALETTE.goldGlow}`, borderRadius: 14, padding: 12, display: "flex", flexDirection: "column", gap: 10, color: PALETTE.slate3, maxWidth: "100%", boxSizing: "border-box" },
  head: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" },
  tagline: { color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 10.5, letterSpacing: "0.18em" },
  title: { color: PALETTE.goldBright, margin: 0, fontSize: 16 },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 10 },
  card: { background: "#040608", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 12, padding: 10, display: "flex", flexDirection: "column", gap: 8 },
  cardHead: { display: "flex", alignItems: "center", gap: 8 },
  cardIcon: { fontSize: 20 },
  cardTitle: { color: PALETTE.goldBright, fontSize: 13 },
  summary: { color: PALETTE.slate3, fontSize: 12.5, lineHeight: 1.55, margin: 0 },
  meta: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 },
  metaLbl: { color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 9.5, letterSpacing: "0.10em" },
  metaVal: { color: PALETTE.slate3, fontSize: 11, lineHeight: 1.45, margin: "2px 0 0 0" },
  cardFoot: {},
  compliance: { color: PALETTE.slate5, fontFamily: "'Space Mono', monospace", fontSize: 9.5 },
};
